import React, { Component } from 'react';
import moment, { Moment } from 'moment';
import { GridView } from 'models/GridView';
import FormItem from 'antd/lib/form/FormItem';
import { PushNotification } from 'utils/PushNotification';
import { AiStudioAgentPlanRequest } from 'models/request/AiStudioAgentPlanRequest';
import { AiStudioAgentPlanResponse } from 'models/response/AiStudioAgentPlanResponse';
import { AiStudioStatusPlanResponse } from 'models/response/AiStudioStatusPlanResponse';
import { AiStudioAgentPlanTiersResponse } from 'models/response/AiStudioAgentPlanTiersResponse';
import { Card, Select, DatePicker, Button, Modal, Alert, Form, Popconfirm, List, Switch } from 'antd';
import { getCopilotConfiguration, getStudioAgentPlan, getStudioAgentPlanTiers, getStudioStatusPlan, setCopilotConfiguration, setStudioAgentPlanTrial, updateStudioAgentPlan } from 'services/CeberusApiService';
import { ConfigurationRequest } from 'models/request/ConfigurationRequest';
import { JsonDocument } from 'models/Blip/JsonDocument';
import { TenantService } from 'connection/TenantService';

const ListItem = List.Item
const ListItemMeta = List.Item.Meta
const { Option } = Select;
const IS_COPILOT_ENABLED_KEY = 'IsCopilotProofOfConceptEnabled'

interface Props {
  tenantId: string;
  loading?: boolean;
}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  agent?: AiStudioAgentPlanResponse;
  original?: AiStudioAgentPlanResponse;
  plans: AiStudioAgentPlanTiersResponse[];
  isPlanModalVisible: boolean;
  planDraft: string | null;
  statusPlan: AiStudioStatusPlanResponse | null;
  isStatusModalVisible: boolean;
  isCopilotActive: boolean;
}

export class AiAgentStudioComponent extends Component<Props> {
  state: State = {
    isLoading: false,
    isSaving: false,
    agent: undefined,
    original: undefined,
    plans: [],
    isPlanModalVisible: false,
    planDraft: null,
    statusPlan: undefined,
    isStatusModalVisible: false,
    isCopilotActive: false
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prev: Props) {
    if (prev.tenantId !== this.props.tenantId) {
      this.loadData();
    }
  }

  private getGridView = (): Array<GridView> => {
    const { agent, isSaving, plans, statusPlan } = this.state;
    const changed = this.hasChanges();

    if (!agent) {
      return [
        {
          title: 'Trial Plan',
          description:
            <Button
              key="start-trial"
              type="primary"
              shape="round"
              size="small"
              icon="swap"
              onClick={this.handleSetTrialPlan}
            >
              Start a trial plan
            </Button>
        }
      ];
    }

    return [
      {
        key: 'tenant-id',
        title: 'Tenant Id',
        description: agent.TenantId
      },
      {
        key: 'blocked-from',
        title: 'Blocked From',
        description:
          <div className="ai-inline">
            <DatePicker
              allowClear
              value={agent.BlockedFrom ? moment(agent.BlockedFrom) : undefined}
              onChange={this.handleChangeBlockedFrom}
              className="ai-date"
              placeholder="Set date"
              showTime
              format="YYYY-MM-DD HH:mm"
            />
          </div>
      },
      {
        key: 'blocked-until',
        title: 'Blocked Until',
        description:
          <div className="ai-inline">
            <DatePicker
              allowClear
              value={agent.BlockedUntil ? moment(agent.BlockedUntil) : undefined}
              onChange={this.handleChangeBlockedUntil}
              className="ai-date"
              placeholder="Set date"
              showTime
              format="YYYY-MM-DD HH:mm"
            />
          </div>
      },
      {
        title: 'Plan',
        description: [
          plans.find(p => p.PlanId === agent.PlanId)?.Name,
          <Button
            key="change-plan"
            type="primary"
            shape="round"
            size="small"
            icon="edit"
            className="ml4"
            onClick={this.openPlanModal}
          >
            Change Plan
          </Button>
        ]
      },
      {
        title: 'Status',
        description: [
          statusPlan?.reason || "-",
          <Button
            key="check-status"
            type="primary"
            shape="round"
            size="small"
            icon="eye"
            className="ml4"
            onClick={this.openStatusModal}
          >
            Check Status
          </Button>
        ]
      },
      {
        title: 'Actions',
        description:
          <Popconfirm
            key="confirm"
            title="This action will update the plan. Are you sure you want to proceed?"
            onConfirm={this.handleSave}
            okText="Yes"
            cancelText="No"
          >
            <Button
              key="update-agent"
              type="primary"
              disabled={!changed || isSaving}
              loading={isSaving}
            >
              Update Plan
            </Button>
          </Popconfirm>
      }
    ];
  }

  private getCopilotGridView = (): Array<GridView> => {
    const { isCopilotActive, isSaving } = this.state;
    return [
      {
        title: 'Is this customer under a custom Copilot plan?',
        description: <Switch 
          checked={!!isCopilotActive} 
          loading={isSaving} 
          onChange={this.handleCopilotSwitchChange}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          />
      }
    ];
  }

  private closePlanModal = () => {
    this.setState({ isPlanModalVisible: false });
  };

  private closeStatusModal = () => {
    this.setState({ isStatusModalVisible: false });
  };

  private confirmPlanChange = async () => {
    const { planDraft, agent } = this.state;
    try {
      this.setState({ isSaving: true });

      const req = new AiStudioAgentPlanRequest();
      req.TenantId = agent.TenantId;
      req.ClusterName = TenantService.getSelectedTenant().name
      req.PlanId = planDraft;
      req.BlockedFrom = agent.BlockedFrom ? new Date(agent.BlockedFrom) : null;
      req.BlockedUntil = agent.BlockedUntil ? new Date(agent.BlockedUntil) : null;

      await updateStudioAgentPlan(req);

      this.setState((prev: State) => {
        if (!prev.agent) return null;
        const updated = { ...prev.agent, PlanId: planDraft };
        return {
          agent: updated,
          original: { ...updated },
          isPlanModalVisible: false
        };
      });
      PushNotification.success('Plan updated successfully.');
    } catch {
      PushNotification.error('Failed to update plan.');
    } finally {
      this.setState({ isSaving: false });
    }
  };

  private hasChanges(): boolean {
    const { agent, original } = this.state;
    if (!agent || !original) return false;
    return (
      agent.PlanId !== original.PlanId ||
      (agent.BlockedFrom || null) !== (original.BlockedFrom || null) ||
      (agent.BlockedUntil || null) !== (original.BlockedUntil || null)
    );
  }

  private getConfigurationCopilotPlan = async (): Promise<boolean> => {
    const configurationRequest = new ConfigurationRequest();
    configurationRequest.ClusterName = TenantService.getSelectedTenant().name;
    configurationRequest.Caller = this.props.tenantId;
    const { IsCopilotProofOfConceptEnabled } = await getCopilotConfiguration(configurationRequest);
    return IsCopilotProofOfConceptEnabled === true || IsCopilotProofOfConceptEnabled === "True";
  }

  private async loadData() {
    const { tenantId } = this.props;
    this.setState({ isLoading: true });

    if (!tenantId) {
      this.setState({ agent: undefined, original: undefined, plans: [], planDraft: null, isLoading: false });
      return;
    }
    try {
      const req = new AiStudioAgentPlanRequest();
      req.TenantId = tenantId;
      req.ClusterName = TenantService.getSelectedTenant().name;
      const [agentResp, plansResp, statusResp] = await Promise.all([
        getStudioAgentPlan(req),
        getStudioAgentPlanTiers(req),
        getStudioStatusPlan(req)
      ]);

      const agent: AiStudioAgentPlanResponse = {
        TenantId: agentResp.tenantId,
        PlanId: agentResp.planId || null,
        BlockedFrom: agentResp.blockedFrom || null,
        BlockedUntil: agentResp.blockedUntil || null
      };

      const plans: AiStudioAgentPlanTiersResponse[] = (plansResp || []).map((p: any) => ({
        PlanId: p.planId || p.PlanId,
        Name: p.name || p.Name
      }));

      this.setState({ agent, original: { ...agent }, plans, planDraft: agent.PlanId, statusPlan: statusResp });
    } catch {
      PushNotification.error('No plan found for this organization.');
    } finally {
      this.setState({ isLoading: false });
    }
    
    this.setState({ isCopilotActive: await this.getConfigurationCopilotPlan() });
  }

  private updateAgent(patch: Partial<AiStudioAgentPlanResponse>) {
    this.setState((prev: State) => prev.agent ? { agent: { ...prev.agent, ...patch } } : null);
  }

  private openPlanModal = () => {
    const { agent } = this.state;
    this.setState({
      isPlanModalVisible: true,
      planDraft: agent ? agent.PlanId : null
    });
  };

  private openStatusModal = async () => {
    const req = new AiStudioAgentPlanRequest();
    req.TenantId = this.props.tenantId;
    req.ClusterName = TenantService.getSelectedTenant().name;
    const statusResp = await getStudioStatusPlan(req);
    this.setState({
      isStatusModalVisible: true,
      statusPlan: statusResp
    });
  };

  private handlePlanDraftChange = (value: string) => {
    this.setState({ planDraft: value });
  };

  private handleChangeBlockedFrom = (date: Moment | null) => {
    this.updateAgent({ BlockedFrom: date ? date.toDate() : null });
  };

  private handleChangeBlockedUntil = (date: Moment | null) => {
    this.updateAgent({ BlockedUntil: date ? date.toDate() : null });
  };

  private handleSave = async () => {
    const { agent } = this.state;
    if (!agent || !this.hasChanges()) return;
    try {
      this.setState({ isSaving: true });
      const req = new AiStudioAgentPlanRequest();
      req.TenantId = agent.TenantId;
      req.ClusterName = TenantService.getSelectedTenant().name;
      req.PlanId = agent.PlanId;
      req.BlockedFrom = agent.BlockedFrom ? new Date(agent.BlockedFrom) : null;
      req.BlockedUntil = agent.BlockedUntil ? new Date(agent.BlockedUntil) : null;
      await updateStudioAgentPlan(req);
      this.setState({ original: { ...agent } });
      PushNotification.success('Updated successfully.');
    } catch {
      PushNotification.error('Failed to save.');
    } finally {
      this.setState({ isSaving: false });
    }
  };
  
  private handleSetTrialPlan = async () => {
    const { tenantId } = this.props;
    try {
      this.setState({ isSaving: true });
      const req = new AiStudioAgentPlanRequest();
      req.TenantId = tenantId;
      req.ClusterName = TenantService.getSelectedTenant().name;
      await setStudioAgentPlanTrial(req);
      PushNotification.success('Trial plan set successfully.');
      await this.loadData();
    } catch {
      PushNotification.error('Failed to set trial plan.');
    } finally {
      this.setState({ isSaving: false });
    }
  };

  private handleCopilotSwitchChange = async (checked: boolean) => {
    this.setState({ isSaving: true });
    try {
      const configurationRequest = new ConfigurationRequest();
      configurationRequest.Caller = this.props.tenantId;
      configurationRequest.ClusterName = TenantService.getSelectedTenant().name;
      configurationRequest.Resource = new JsonDocument(IS_COPILOT_ENABLED_KEY, checked);
      await setCopilotConfiguration(configurationRequest);
      PushNotification.success('Copilot plan activated successfully.');
      await this.loadData();
    } catch {
      PushNotification.error('Copilot plan activation failed.');
    } finally {
      this.setState({ isSaving: false });
    }
  };

  render() {
    const {
      isLoading,
      isPlanModalVisible,
      isStatusModalVisible,
      plans,
      planDraft,
      statusPlan,
    } = this.state;

    const gridView = this.getGridView();
    const gridViewCopilot = this.getCopilotGridView();

    return (
      <div className="mt5 w-100">
        <Card title="Marketing & AI Products Plan">
          <List
            dataSource={gridView}
            loading={isLoading}
            key="ai-agent-studio-list"
            renderItem={item => (
              <ListItem >
                <ListItemMeta
                  title={item.title}
                  description={item.description}>
                </ListItemMeta>
              </ListItem>
            )}
          />
          <Modal
            centered
            visible={isPlanModalVisible}
            title="Select a new plan"
            okText="Change"
            onCancel={this.closePlanModal}
            onOk={this.confirmPlanChange}
          >
            <Form layout="vertical">
              <FormItem hasFeedback>
                Plan selection is required.
                <Select placeholder="Select a plan"
                  onChange={this.handlePlanDraftChange}
                  showSearch
                  optionFilterProp="children"
                  value={planDraft || undefined}
                >
                  {plans.map(plan => (
                    <Option key={plan.PlanId} value={plan.PlanId}>
                      {plan.Name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            </Form>
            <Alert
              message="Careful!"
              description="This action will change the current plan!"
              type="warning"
              showIcon
            />
          </Modal>
          <Modal
            centered
            visible={isStatusModalVisible}
            title="Current Status Plan"
            footer={null}
            onCancel={this.closeStatusModal}
          >
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(statusPlan, null, 2)}
            </pre>
          </Modal>
        </Card>
        <Card title="Copilot Exceptional Plans" className="mt5">
          <List
            dataSource={gridViewCopilot}
            loading={isLoading}
            key="copilot-exceptional-plans"
            renderItem={item => (
              <ListItem >
                <ListItemMeta
                  title={item.title}
                  description={item.description}>
                </ListItemMeta>
              </ListItem>
            )}
          />
        </Card>
      </div>
    );
  }
}